import React from 'react';
import classNames from 'classnames';
import {TwitterFollowButton} from "anvil_social_src/components";
import moment from "moment";


/**
 * Component for rendering byline information for use on a content page.
 *
 * @param {string} publishedDate - The date the page was published.
 * @param {string} updatedDate - The date the page was updated at.
 * @param {string} authorName - The full name of the author.
 * @param {string} publication - The name of the publication the author is writing for.
 * @param {string} profilePicture - The url of the profile picture to use for the author.
 * @param {string} profilePictureStyle - The style of the profile picture image, options are: 'square', 'rounded'
 *  and 'circle', defaults to 'circle'
 * @param {string} authorPageUrl - The url to navigate to when the profile picture and author name are clicked.
 * @param {string} twitterHandle - The twitter handle for the author without the `@` symbol.
 * @param {string} sassPrefix - The sass prefix to add to the class names.
 * @param {string} email - The email of the author.
 * @param {string} externalLinkName - Name of the external link, used in conjuction with externalLinkUrl.
 * @param {string} externalLinkUrl - Link to an external site.
 */
export function Byline({
                           publishedDate,
                           updatedDate,
                           authorName = '',
                           publication = '',
                           profilePicture = '',
                           profilePictureStyle = 'circle',
                           authorPageUrl = '',
                           twitterHandle = '',
                           sassPrefix = '',
                           email = '',
                           externalLinkName = '',
                           externalLinkUrl = '',
                       }) {
    let contentModifiedPrefix = '';
    let publishedDateString = '';
    let contentPublishedMessage = '';
    let updatedDateString = '';
    const momentUpdatedDate = moment(updatedDate);
    const momentPublishedDate = moment(publishedDate);

    if (momentUpdatedDate.isValid()) {
        updatedDateString = momentUpdatedDate.format('MMM D, YYYY, h:mm A');
    }
    if (momentPublishedDate.isValid()) {
        publishedDateString = momentPublishedDate.format('MMM D, YYYY, h:mm A');
    }
    if (momentUpdatedDate.isSame(momentPublishedDate, 'minute')) {
        contentModifiedPrefix = 'Published:';
    } else {
        contentPublishedMessage = `Published: ${publishedDateString}`;
        contentModifiedPrefix = 'Updated:';
    }

    const contentModifiedMessage = `${contentModifiedPrefix} ${updatedDateString}`;

    const bylineClasses = classNames(
        'anvil-byline',
        {'anvil-byline--avatar': profilePicture !== ''},
        {[`anvil-byline--${sassPrefix}`]: sassPrefix !== ''},
    );
    const profilePictureClasses = classNames(
        'anvil-byline__avatar',
        {[`anvil-byline__avatar--${profilePictureStyle}`]: profilePictureStyle !== 'square'},
        'align-self-middle'
    );

    return (
        <div className={bylineClasses}>
            {profilePicture !== '' && (
                <a href={authorPageUrl === '' ? '#' : authorPageUrl}
                   className={profilePictureClasses}
                   style={{backgroundImage: `url('${profilePicture}')`}}>
                </a>
            )}
            <div className="column anvil-byline__text">
                <div className="anvil-byline__link-row">
                    {authorName !== '' && (
                        <>
                            <span>
                                {authorPageUrl !== '' && (
                                    <a className="anvil-byline__link" href={authorPageUrl}>
                                        {authorName}
                                    </a>
                                )}
                                {authorPageUrl === '' && (
                                    authorName
                                )}
                            </span>
                            {twitterHandle !== '' && (
                                <span className="anvil-byline__twitter">
                                    <TwitterFollowButton showScreenName={false} twitterHandle={twitterHandle}/>
                                </span>
                            )}
                        </>
                    )}
                </div>
                {publication !== '' && (
                    <i>{publication}</i>
                )}
                {email !== '' && (
                    <a href={`mailto:${email}`}>{email}</a>
                )}
                {externalLinkUrl !== '' && (
                    <a target="_blank" rel="noreferrer" href={externalLinkUrl}>
                        {externalLinkName !== '' && (
                            externalLinkName
                        )}
                        {externalLinkName === '' && (
                            externalLinkUrl
                        )}
                        <span className="open-in-new-icon"></span>
                    </a>
                )}
                <div className="anvil-byline__date">
                    {contentModifiedMessage}
                </div>
                {contentPublishedMessage !== '' && (
                    <div className="anvil-byline__date">
                        {contentPublishedMessage}
                    </div>
                )}
            </div>
        </div>
    );
}
